import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { JobAttachmentChipType } from 'src/app/shared/common/enums/attachments/job-attachment-type';
import { JobChipData } from 'src/app/shared/survey-data-services/survey-job';

@Component({
    selector: 'job-file-tag-chips',
    templateUrl: 'job-file-tag-chips.component.html'
})
export class JobFileTagChipsComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

    @Input() chipHeader: string = '';
    @Input() files: Array<JobChipData> = [];
    @Input() chipType: JobAttachmentChipType = JobAttachmentChipType.Report;
    @Input() loading: boolean = false;
    @Input() error: boolean = false;
    @Input() noFilesMessage: string = '';
    @Input() disableRipple: boolean = false;
    @Input() cancelAction: boolean = false;

    @Input() inlineMode: boolean = false;

    @Output() chipAction = new EventEmitter<JobChipData>();

    // Make enum available in template
    JobAttachmentChipType = JobAttachmentChipType;
    chipIcon: string;

    // Track which file is currently downloading
    downloadingFile: string | null = null;

    // New properties for overflow handling
    @ViewChild('chipsContainer') chipsContainer: ElementRef;

    // Properties for overflow calculation
    visibleTagsOrFiles: JobChipData[] = [];
    overflowCount: number = 0;
    overflowTitle: string = '';

    // For cleanup
    private destroy$ = new Subject<void>();
    private timeouts: Array<ReturnType<typeof setTimeout>> = [];

    ngOnInit() {
        this.chipIcon = this.getIcon();
        this.visibleTagsOrFiles = [...this.files];
    }

    ngAfterViewInit() {
        // run only if inline mode is enabled
        if (this.inlineMode) {
            // Set up resize listener using RxJS to avoid memory leaks
            this.ngZone.runOutsideAngular(() => {
                fromEvent(window, 'resize')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.ngZone.run(() => {
                            this.calculateVisibleTags();
                        });
                    });
            });

            // Initial calculation
            requestAnimationFrame(() => {
                this.calculateVisibleTags();
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        // Clear all timeouts
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    }

    /**
     * Get icon based on chip type
     * retrieves icon classes from the SVGs - src/styles/_svg-icons.less
     * please maintain this hardcoded as followed in .less
     */
    getIcon(): string {
        switch (this.chipType) {
            case JobAttachmentChipType.Tag:
                return 'tag';
            case JobAttachmentChipType.Report:
                return 'document';
            case JobAttachmentChipType.LinkedProjectData:
            case JobAttachmentChipType.Other:
            default:
                return 'file';
        }
    }

    handleCancel(file: JobChipData): void {
        this.chipAction.emit(file);
    }

    /**
     * Handle file click with download indicator to prevent spam clicks
     */
    handleFileClick(file: JobChipData, event?: MouseEvent): void {
        // Prevent multiple clicks while downloading
        if (
            this.downloadingFile === file.label ||
            this.disableRipple ||
            this.JobAttachmentChipType.EditLinkedProjectData == this.chipType
        ) {
            return;
        }

        if (file.url) {
            // Set downloading state
            this.downloadingFile = file.label;

            // Start download after a short delay (optional)
            const downloadTimeout = setTimeout(() => {
                this.downloadFile(file.url, file.label);

                // Reset downloading state after a reasonable timeout
                // This ensures the spinner disappears even if download fails
                const resetTimeout = setTimeout(() => {
                    this.downloadingFile = null;
                }, 2000);
                this.timeouts.push(resetTimeout);
            }, 200);
            this.timeouts.push(downloadTimeout);
        }
    }

    /**
     * Helper method to download a file
     */
    private downloadFile(url: string, fileName: string): void {
        // Create a hidden anchor element
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'download';
        link.style.display = 'none';

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Check if a specific file is currently downloading
     */
    isDownloading(fileName: string): boolean {
        return this.downloadingFile === fileName;
    }

    /**
     * Calculate which tags should be visible based on available width
     */
    calculateVisibleTags(): void {
        if (!this.chipsContainer || !this.files.length || !this.inlineMode) {
            return;
        }

        // Reset to show all tags initially
        this.visibleTagsOrFiles = [...this.files];
        this.overflowCount = 0;

        // Use a single change detection cycle before measuring
        this.cdr.detectChanges();

        const calcTimeout = setTimeout(() => {
            // Cache DOM queries to avoid repeated access
            const containerWidth = this.chipsContainer.nativeElement.clientWidth;
            if (containerWidth <= 0) {
                return;
            }

            const chips = this.chipsContainer.nativeElement.querySelectorAll('mat-chip');
            if (!chips.length) {
                return;
            }

            // Create an array of chip widths for faster processing
            const chipWidths = Array.from(chips)
                .filter(chip => !(chip as HTMLElement).classList.contains('overflow-chip'))
                .map(chip => (chip as HTMLElement).getBoundingClientRect().width + 8); // Width + gap

            // Early exit: If all chips fit within container, keep showing all
            const totalWidth = chipWidths.reduce((sum, width) => sum + width, 0);

            // No change needed - already showing all tags
            if (totalWidth <= containerWidth) {
                return;
            }

            const overflowChipWidth = 50;
            let usedWidth = 0;
            let visibleCount = 0;

            for (let i = 0; i < chipWidths.length && i < this.files.length; i++) {
                const chipWidth = chipWidths[i];
                const isLastChip = i === this.files.length - 1;

                // Only reserve space for overflow chip if there would be more tags after this one
                const needsOverflow = !isLastChip;
                const effectiveRemainingWidth = containerWidth - usedWidth - (needsOverflow ? overflowChipWidth : 0);

                if (chipWidth > effectiveRemainingWidth) {
                    break;
                }

                usedWidth += chipWidth;
                visibleCount++;
            }

            // Only update the component state and trigger change detection if something changed
            const newOverflowCount = this.files.length - visibleCount;
            if (newOverflowCount !== this.overflowCount || visibleCount !== this.visibleTagsOrFiles.length) {
                // Update component state
                this.visibleTagsOrFiles = this.files.slice(0, visibleCount);
                this.overflowCount = newOverflowCount;

                this.updateOverflowTitle();

                // Run change detection only once
                this.cdr.detectChanges();
            }
        }, 0);

        // track timeouts for cleanup
        this.timeouts.push(calcTimeout);
    }

    /**
     * Get title for the overflow chip containing all hidden tags
     */
    getOverflowTitle(): string {
        if (this.overflowCount <= 0) {
            return '';
        }

        return this.files
            .slice(this.visibleTagsOrFiles.length)
            .map(file => file.label)
            .join(', ');
    }

    /**
     * Generate overflow title string for the overflow chip
     * This should be called during change detection to avoid calling in template
     */
    updateOverflowTitle(): void {
        this.overflowTitle = this.getOverflowTitle();
    }
}
